'use strict';

var FirebaseIndirectArray = require('./FirebaseIndirectArray');
var KayleeBase = require('./KayleeBase');
var KayleeCache = require('./KayleeCache');
var KayleeConnectionState = require('./KayleeConnectionState');
var KayleeSite = require('./KayleeSite');
var KayleeUser = require('./KayleeUser');
var KayleeUtil = require('./KayleeUtil');
var log = require('../log/Log').channel('KayleeSites');

var KayleeSites = {
    fromJson: fromJson,
    getSites: getSites,
};

function fromJson(data) {
    var reqProps = [ '$companyCacheKey', '$userId' ];
    return FirebaseIndirectArray.$fromJson(data, 'Sites', reqProps, reqProps,
        function(data) {
            var company = KayleeCache.get(data.$companyCacheKey);
            return getSites(company, data.$userId);
        }
    );
}

// Returns a promise that resolves to a Sites object that contains the sites
// accessible to the specified user for the specified company. If no user is
// specified, then the sites will be for the current user.

function getSites(company, userId) {
    if (typeof userId === 'undefined' || userId === null) {
        if (KayleeBase.app.auth().currentUser === null) return null;
        userId = KayleeBase.app.auth().currentUser.uid;
    }

    var key = 'Sites: ' + company.$id + ' ' + userId;
    var sites = KayleeCache.get(key);

    if (sites === null) {
        sites = new Sites(company, userId);
        KayleeCache.put(key, sites);
    }

    return sites;
}

function Sites(company, userId) {
    var self = this;

    FirebaseIndirectArray.call(self, null, KayleeSite.getSite);
    self.$company = company;
    self.$user = KayleeUser.getUser(userId);
    KayleeUtil.bindPrototypeMethods(Sites.prototype, self, self.$items);
    this.$items.$self = this;

    if (KayleeConnectionState.isWorkingOnline()) {
        self.$user.$loaded()
        .then(function() {
            var srcRef = self.$getSrcRef(
                self.$user.$canWatchAllSitesForCompany(company.$id));
            self.$setSrcRef(srcRef);

            if (KayleeBase.isDbRoleEnabled) {
                self.$user.$ref.child('roles/role').on('value', self.$dbRoleChangedCb,
                    self.$dbRoleCancelledCb, self);
            }

            self.$user.$ref.child('roles/companies/' + company.$id + '/role')
            .on('value', self.$companyRoleChangedCb,
                self.$companyRoleCancelledCb, self);
        });
    }

    return self.$items;
}

Sites.prototype = Object.create(FirebaseIndirectArray.prototype);
Sites.prototype.constructor = Sites;

Sites.prototype.$toJson = function() {
    return FirebaseIndirectArray.prototype.$toJson.call(this,
        [], { $companyCacheKey: this.$company.$cacheKey, $userId: this.$user.$id });
};

Sites.prototype.toString = function() {
    return '[Sites for ' + this.$user + ' in ' + this.$company + ']';
};

Sites.prototype.$getSrcRef = function(canWatchAllSitesForCompany) {
    return canWatchAllSitesForCompany ?
        KayleeBase.app.database().ref('index/sitesByCompany/' + this.$company.$id) :
        this.$user.$ref.child('roles/companies/' + this.$company.$id + '/sites');
};

Sites.prototype.$dbRoleChangedCb = function(snap) {
    var newDbRole = snap.val();
    var oldWatchAll = this.$user.$canWatchAllSitesForCompany(this.$company.$id);
    var newWatchAll = this.$user.$canWatchAllSitesForCompany(
        this.$company.$id, newDbRole);

    if (this.$user.$getRoleForDb() !== newDbRole) {
        log.debug('db role changed to ' + newDbRole + ' for ' + this.$user +
            ' in ' + this.$company);
    }

    if (oldWatchAll !== newWatchAll) {
        this.$setSrcRef(this.$getSrcRef(newWatchAll));
    }
};

Sites.prototype.$dbRoleCancelledCb = function(err) {
    //log.debug('db role cancelled for ' + this.$user + ', error: ' + err);

    // FirebaseIndirectArray takes care of cleaning up itself when the
    // subscription for its srcRef is cancelled, then KayleeCache takes care of
    // removing the array from the cache via FirebaseIndirectArray's $onDestroy.
    // All we need to do is stop watching the db role.

    this.$user.$ref.child('roles/role')
    .off('value', this.$dbRoleChangedCb, this);
};

Sites.prototype.$companyRoleChangedCb = function(snap) {
    var newCompanyRole = snap.val();
    var dbRole = this.$user.$getRoleForDb();
    var oldWatchAll = this.$user.$canWatchAllSitesForCompany(
        this.$company.$id, dbRole);
    var newWatchAll = this.$user.$canWatchAllSitesForCompany(
        this.$company.$id, dbRole, newCompanyRole);

    if (this.$user.$getRoleForCompany(this.$company) !== newCompanyRole) {
        log.debug('company role changed to ' + newCompanyRole + ' for ' +
            this.$user + ' in ' + this.$company);
    }

    if (oldWatchAll !== newWatchAll) {
        this.$setSrcRef(this.$getSrcRef(newWatchAll));
    }
};

Sites.prototype.$companyRoleCancelledCb = function(err) {
    //log.debug('company role cancelled for ' + this.$user + ' on ' +
    //    this.$company + ', error: ' + err);

    // FirebaseIndirectArray takes care of cleaning up itself when the
    // subscription for its srcRef is cancelled, then KayleeCache takes care of
    // removing the array from the cache via FirebaseIndirectArray's $onDestroy.
    // All we need to do is stop watching the company role.

    this.$user.$ref.child('roles/companies/' + this.$company.$id + '/role')
    .off('value', this.$companyRoleChangedCb, this);

};

Sites.prototype.$getAlertCount = function() {
    var alertCount = 0;

    this.$items.forEach(function(site) {
        alertCount += site.$getAlertCount();
    });

    return alertCount;
};

Sites.prototype.$getControllerCount = function() {
    var controllerCount = 0;

    this.$items.forEach(function(site) {
        controllerCount += site.$getControllerCount();
    });

    return controllerCount;
};

Sites.prototype.$getInCallCount = function() {
    var inCallCount = 0;

    this.$items.forEach(function(site) {
        inCallCount += site.$getInCallCount();
    });

    return inCallCount;
};

Sites.prototype.$getRoomCount = function() {
    var roomCount = 0;

    this.$items.forEach(function(site) {
        roomCount += site.$getRoomCount();
    });

    return roomCount;
};

module.exports = KayleeSites;
